/**
 * The five `portal_*` functions, as the portal calls them (PT-06, V5, docs/15).
 *
 * The portal reads and writes nothing else. Every table is closed to a
 * customer by row-level security, and these functions are `security definer`
 * with a fixed projection - so this file is the whole of the portal's reach
 * into the database, and the whole of what a customer can learn from it.
 *
 * Since V5 the page offers what is in stock rather than a curated list, and it
 * never learns a stock figure: `portal_availability` names the plants in stock
 * and says nothing about how many. So nothing here reads a quantity on offer,
 * and a field the server might one day add beside an item is not carried
 * through to the page by accident.
 *
 * Each answer is JSON the function built. It is read here into a typed shape,
 * defensively: a field that is missing or the wrong type reads as absent
 * rather than throwing in the middle of a customer's basket. A request nobody
 * should send comes back as an error with a code (docs/15):
 *
 * - `PT403` - this login has no portal access (disabled, removed, or staff);
 * - `PT404` - somebody else's order, or none;
 * - `22023` - a malformed request, or an order id this login already used
 *   for a different order. The second is read apart from the first: it says
 *   an earlier attempt at the order went through (`placing.ts`).
 *
 * The one answer read strictly is placing an order. It has one success,
 * `placed`, with a reference. Anything else - the `short` and `closed` answers
 * the curated list used to give among them - is a failure, so the basket is
 * kept and the customer tries again, rather than the page telling them
 * something about their order that the server never said.
 */

/** The part of a Supabase client this file uses, so a test can stand in for it. */
export interface RpcClient {
  rpc(
    fn: string,
    args?: Record<string, unknown>,
  ): PromiseLike<{
    data: unknown;
    error: { readonly code?: string; readonly message: string } | null;
  }>;
}

export type PortalError =
  | { readonly kind: 'no-access' }
  | { readonly kind: 'not-found' }
  /** This id already placed a different order: an earlier attempt went through. */
  | { readonly kind: 'id-used' }
  | { readonly kind: 'refused'; readonly message: string }
  | { readonly kind: 'failed'; readonly message: string };

export type Answer<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: PortalError };

export interface Address {
  readonly id: string;
  readonly line1: string;
  readonly line2?: string;
  readonly suburb?: string;
  readonly city?: string;
  readonly province?: string;
  readonly postalCode?: string;
  readonly instructions?: string;
  readonly isDefault: boolean;
}

export interface Me {
  readonly account: {
    readonly customerId: string;
    readonly accountCode: string;
    readonly tradingName: string;
  };
  readonly contact: { readonly name?: string; readonly email: string };
  readonly addresses: readonly Address[];
}

/** A plant the nursery has in stock, as a customer sees it: without a figure. */
export interface ListedItem {
  readonly productId: string;
  readonly sku: string;
  readonly description: string;
  readonly unit?: string;
  readonly sizeGrade?: string;
  readonly variety?: string;
  readonly categoryId?: string;
  readonly category?: string;
}

export interface Category {
  readonly id: string;
  readonly name: string;
}

export interface PriceRow {
  readonly id: string;
  readonly productId: string;
  readonly priceCents: number;
  readonly effectiveFrom: string;
  readonly effectiveTo?: string;
}

export interface Availability {
  /** In the server's order: by category, then description. */
  readonly items: readonly ListedItem[];
  /** The categories with at least one plant in stock, by name. */
  readonly categories: readonly Category[];
  readonly prices: {
    readonly hasTier: boolean;
    readonly taxInclusive: boolean;
    readonly tier: readonly PriceRow[];
    readonly overrides: readonly PriceRow[];
  };
}

export interface PlaceRequest {
  /**
   * Minted by the portal: the idempotency key. A retry of the same order keeps
   * it, and a changed order gets a new one once the old is known not to have
   * gone (`placing.ts`).
   */
  readonly id: string;
  readonly lines: readonly {
    readonly product_id: string;
    readonly quantity: string;
    readonly shown_unit_price_cents?: number;
  }[];
  readonly delivery_address_id?: string;
  readonly required_date?: string;
  readonly note?: string;
}

export interface Placed {
  readonly status: 'placed';
  readonly webOrderId: string;
  readonly reference: string;
}

export type WebOrderStatus = 'received' | 'confirmed' | 'declined' | 'cancelled';

export interface WebOrderLine {
  readonly lineNo: number;
  readonly productId?: string;
  readonly description: string;
  /** What the customer asked for, as the server writes it: `10.000`. */
  readonly quantity: string;
  /**
   * What the nursery will supply, once it has confirmed the order: `8.000`, or
   * `0.000` when it supplies none of it. Absent until then.
   */
  readonly confirmedQuantity?: string;
  readonly shownUnitPriceCents?: number;
}

export interface WebOrder {
  readonly id: string;
  readonly reference: string;
  readonly status: WebOrderStatus;
  readonly placedAt: string;
  readonly requiredDate?: string;
  readonly note?: string;
  readonly decisionReason?: string;
  readonly decidedAt?: string;
  /** The nursery's order number, once confirmed. */
  readonly orderNumber?: string;
  readonly orderStatus?: string;
  readonly lines: readonly WebOrderLine[];
}

export type CancelAnswer =
  | { readonly status: 'cancelled' }
  | { readonly status: 'decided'; readonly orderStatus: string };

// --- Reading JSON -------------------------------------------------------------

type Json = Readonly<Record<string, unknown>>;

function record(value: unknown): Json {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Json)
    : {};
}

function list(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : [];
}

function text(value: unknown): string | undefined {
  if (typeof value === 'string') return value === '' ? undefined : value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return undefined;
}

function whole(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isSafeInteger(value)) return value;
  if (typeof value === 'string' && /^-?\d+$/.test(value)) {
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) ? parsed : undefined;
  }
  return undefined;
}

/** `{ ...(x === undefined ? {} : { key: x }) }`, which `exactOptionalPropertyTypes` needs. */
function optional<K extends string, V>(
  key: K,
  value: V | undefined,
): Partial<Record<K, V>> {
  return (value === undefined ? {} : { [key]: value }) as Partial<Record<K, V>>;
}

const STATUSES: readonly WebOrderStatus[] = [
  'received',
  'confirmed',
  'declined',
  'cancelled',
];

function statusOf(value: unknown): WebOrderStatus {
  return STATUSES.find((status) => status === value) ?? 'received';
}

export function readMe(data: unknown): Me {
  const root = record(data);
  const account = record(root['account']);
  const contact = record(root['contact']);
  return {
    account: {
      customerId: text(account['customer_id']) ?? '',
      accountCode: text(account['account_code']) ?? '',
      tradingName: text(account['trading_name']) ?? '',
    },
    contact: {
      ...optional('name', text(contact['name'])),
      email: text(contact['email']) ?? '',
    },
    addresses: list(root['addresses']).map((raw) => {
      const address = record(raw);
      return {
        id: text(address['id']) ?? '',
        line1: text(address['line1']) ?? '',
        ...optional('line2', text(address['line2'])),
        ...optional('suburb', text(address['suburb'])),
        ...optional('city', text(address['city'])),
        ...optional('province', text(address['province'])),
        ...optional('postalCode', text(address['postal_code'])),
        ...optional('instructions', text(address['instructions'])),
        isDefault: address['is_default'] === true,
      };
    }),
  };
}

function readPriceRows(value: unknown): PriceRow[] {
  const rows: PriceRow[] = [];
  for (const raw of list(value)) {
    const row = record(raw);
    const id = text(row['id']);
    const productId = text(row['product_id']);
    const priceCents = whole(row['price_cents']);
    const effectiveFrom = text(row['effective_from']);
    // A row that cannot be priced from is left out rather than priced as zero.
    if (
      id === undefined ||
      productId === undefined ||
      priceCents === undefined ||
      effectiveFrom === undefined
    ) {
      continue;
    }
    rows.push({
      id,
      productId,
      priceCents,
      effectiveFrom: effectiveFrom.slice(0, 10),
      ...optional('effectiveTo', text(row['effective_to'])?.slice(0, 10)),
    });
  }
  return rows;
}

function readItems(value: unknown): ListedItem[] {
  const items: ListedItem[] = [];
  const seen = new Set<string>();
  for (const raw of list(value)) {
    const item = record(raw);
    const productId = text(item['product_id']);
    // The basket is keyed by product. An item without one could not be
    // ordered, and a second copy of one would be two boxes for one line.
    if (productId === undefined || seen.has(productId)) continue;
    seen.add(productId);
    items.push({
      productId,
      sku: text(item['sku']) ?? '',
      description: text(item['description']) ?? '',
      ...optional('unit', text(item['unit'])),
      ...optional('sizeGrade', text(item['size_grade'])),
      ...optional('variety', text(item['variety'])),
      ...optional('categoryId', text(item['category_id'])),
      ...optional('category', text(item['category'])),
    });
  }
  return items;
}

function readCategories(value: unknown): Category[] {
  const categories: Category[] = [];
  for (const raw of list(value)) {
    const category = record(raw);
    const id = text(category['id']);
    const name = text(category['name']);
    if (id !== undefined && name !== undefined) categories.push({ id, name });
  }
  return categories;
}

export function readAvailability(data: unknown): Availability {
  const root = record(data);
  const prices = record(root['prices']);
  return {
    items: readItems(root['items']),
    categories: readCategories(root['categories']),
    prices: {
      hasTier: prices['has_tier'] === true,
      taxInclusive: prices['tax_inclusive'] === true,
      tier: readPriceRows(prices['tier']),
      overrides: readPriceRows(prices['overrides']),
    },
  };
}

/** An answer this page does not know how to read. `call` makes it a failure. */
export class UnexpectedAnswer extends Error {
  constructor(what: string) {
    super(`the nursery answered ${what}, which this page does not know`);
    this.name = 'UnexpectedAnswer';
  }
}

/**
 * The answer to placing an order. Only `placed`, with both of its ids, is a
 * success; anything else throws, and `call` turns the throw into a failure.
 */
export function readPlaceAnswer(data: unknown): Placed {
  const root = record(data);
  const status = root['status'];
  if (status !== 'placed') {
    throw new UnexpectedAnswer(
      typeof status === 'string' ? `"${status}"` : 'without a status',
    );
  }
  const webOrderId = text(root['web_order_id']);
  const reference = text(root['reference']);
  if (webOrderId === undefined || reference === undefined) {
    throw new UnexpectedAnswer('"placed" without the order\'s reference');
  }
  return { status: 'placed', webOrderId, reference };
}

export function readOrders(data: unknown): WebOrder[] {
  return list(data).map((raw) => {
    const order = record(raw);
    return {
      id: text(order['id']) ?? '',
      reference: text(order['reference']) ?? '',
      status: statusOf(order['status']),
      placedAt: text(order['placed_at']) ?? '',
      ...optional('requiredDate', text(order['required_date'])),
      ...optional('note', text(order['note'])),
      ...optional('decisionReason', text(order['decision_reason'])),
      ...optional('decidedAt', text(order['decided_at'])),
      ...optional('orderNumber', text(order['order_number'])),
      ...optional('orderStatus', text(order['order_status'])),
      lines: list(order['lines']).map((rawLine) => {
        const line = record(rawLine);
        return {
          lineNo: whole(line['line_no']) ?? 0,
          ...optional('productId', text(line['product_id'])),
          description: text(line['description']) ?? '',
          quantity: text(line['quantity']) ?? '0',
          ...optional('confirmedQuantity', text(line['confirmed_quantity'])),
          ...optional('shownUnitPriceCents', whole(line['shown_unit_price_cents'])),
        };
      }),
    };
  });
}

export function readCancelAnswer(data: unknown): CancelAnswer {
  const root = record(data);
  if (root['status'] === 'cancelled') return { status: 'cancelled' };
  return { status: 'decided', orderStatus: text(root['order_status']) ?? '' };
}

// --- Calling ------------------------------------------------------------------

/**
 * How the server words its refusal of a different order under an id already
 * placed. It shares `22023` with a malformed request, and means the opposite:
 * nothing is wrong with the request, and the order under that id exists.
 */
const ID_USED = 'used for a different order';

function errorOf(error: {
  readonly code?: string;
  readonly message: string;
}): PortalError {
  if (error.code === 'PT403') return { kind: 'no-access' };
  if (error.code === 'PT404') return { kind: 'not-found' };
  if (error.code === '22023' && error.message.includes(ID_USED))
    return { kind: 'id-used' };
  if (error.code === '22023') return { kind: 'refused', message: error.message };
  return { kind: 'failed', message: error.message };
}

async function call<T>(
  client: RpcClient,
  fn: string,
  args: Record<string, unknown> | undefined,
  read: (data: unknown) => T,
): Promise<Answer<T>> {
  try {
    const { data, error } = await client.rpc(fn, args);
    if (error !== null) return { ok: false, error: errorOf(error) };
    return { ok: true, value: read(data) };
  } catch (cause: unknown) {
    // A dropped connection, or an answer `read` would not accept.
    return {
      ok: false,
      error: {
        kind: 'failed',
        message: cause instanceof Error ? cause.message : String(cause),
      },
    };
  }
}

export interface PortalApiHooks {
  /**
   * A call was refused because this login no longer has portal access: the
   * nursery turned it off while the page was open. Whichever call meets it
   * first, the page signs out rather than leaving a customer ordering into a
   * refusal.
   */
  readonly onNoAccess?: () => void;
}

/** The portal's calls, bound to one signed-in client. */
export function portalApi(client: RpcClient, hooks: PortalApiHooks = {}) {
  const watched = async <T>(answer: Promise<Answer<T>>): Promise<Answer<T>> => {
    const settled = await answer;
    if (!settled.ok && settled.error.kind === 'no-access') hooks.onNoAccess?.();
    return settled;
  };
  return {
    me: () => watched(call(client, 'portal_me', undefined, readMe)),
    availability: () =>
      watched(call(client, 'portal_availability', undefined, readAvailability)),
    // PostgREST passes a function's arguments by name.
    place: (request: PlaceRequest) =>
      watched(call(client, 'portal_place_order', { request }, readPlaceAnswer)),
    orders: () => watched(call(client, 'portal_orders', undefined, readOrders)),
    cancel: (webOrderId: string) =>
      watched(
        call(
          client,
          'portal_cancel_order',
          { web_order: webOrderId },
          readCancelAnswer,
        ),
      ),
  };
}

export type PortalApi = ReturnType<typeof portalApi>;
